(function () {
  'use strict';

  const POPUP_ID     = 'halo-quickshop-popup-option-3';
  const OPEN_CLASS   = 'quickshop-popup-show';
  const TRIGGER_ATTR = 'data-quickshop-popup';
  const HANDLE_ATTR  = 'data-product-handle';
  const CLOSE_ATTR   = 'data-close-quick-shop-popup';

  const JQUERY_SRC = 'https://code.jquery.com/jquery-3.7.1.min.js';
  const SLICK_JS   = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js';
  const SLICK_CSS  = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css';

  const ARROW_PREV_SVG = '<button type="button" class="slick-prev slick-arrow" aria-label="Previous"><svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M 7.75 1.34375 L 6.25 2.65625 L 14.65625 12 L 6.25 21.34375 L 7.75 22.65625 L 16.75 12.65625 L 17.34375 12 L 16.75 11.34375 Z"></path></svg></button>';
  const ARROW_NEXT_SVG = '<button type="button" class="slick-next slick-arrow" aria-label="Next"><svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M 7.75 1.34375 L 6.25 2.65625 L 14.65625 12 L 6.25 21.34375 L 7.75 22.65625 L 16.75 12.65625 L 17.34375 12 L 16.75 11.34375 Z"></path></svg></button>';

  let isOpen = false;
  let isFetching = false;
  let depsLoaded = false;

  const getPopup   = () => document.getElementById(POPUP_ID);
  const getContent = () => getPopup()?.querySelector('.halo-popup-content');

  function loadStylesheet(href) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if ([...document.scripts].some(s => s.src === src)) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async function loadSlickDeps() {
    if (depsLoaded) return;
    if (!window.jQuery) await loadScript(JQUERY_SRC);
    await Promise.all([loadStylesheet(SLICK_CSS), loadScript(SLICK_JS)]);
    depsLoaded = true;
  }

  function openPopup() {
    document.body.classList.add(OPEN_CLASS);
    const popup = getPopup();
    if (popup) popup.removeAttribute('hidden');
    isOpen = true;
    trapFocus(popup);
  }

  function closePopup() {
    document.body.classList.remove(OPEN_CLASS);
    const popup = getPopup();
    if (popup) popup.setAttribute('hidden', '');
    isOpen = false;
  }

  function trapFocus(container) {
    if (!container) return;
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) focusable[0].focus();
  }

  async function loadQuickShop(handle) {
    if (isFetching) return;
    isFetching = true;

    const content = getContent();
    if (content) content.innerHTML = '<div class="halo-quickshop-loading" aria-live="polite">Loading…</div>';

    const root = window.Shopify?.routes?.root ?? '/';
    const url  = `${root}products/${encodeURIComponent(handle)}?view=ajax_quick_shop`;

    try {
      await loadSlickDeps();

      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      if (content) content.innerHTML = html;

      const popup = getPopup();
      initSlickGallery(popup);
      initThumbnailGallery(popup);
      initVariantOptions(popup);
      initQuantity(popup);
      initAddToCart(popup);

      onOptionChange(popup);

      setTimeout(openPopup, 50);
    } catch (err) {
      if (content) content.innerHTML = '<p class="halo-quickshop-error">Could not load product. Please try again.</p>';
      console.error('[halo-quick-shop]', err);
    } finally {
      isFetching = false;
    }
  }

  function initSlickGallery(scope) {
    if (!scope || !window.jQuery || !window.jQuery.fn.slick) return;
    const $ = window.jQuery;

    const $nav = $(scope).find('.productView-nav').first();
    if (!$nav.length || $nav.hasClass('slick-initialized')) return;

    const slideCount = $nav.children('.productView-image').length;
    if (slideCount === 0) return;

    $nav.slick({
      fade: true,
      dots: false,
      arrows: true,
      infinite: slideCount > 1,
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: false,
      prevArrow: ARROW_PREV_SVG,
      nextArrow: ARROW_NEXT_SVG,
      responsive: [
        { breakpoint: 768, settings: { arrows: true } }
      ]
    });

    $nav.on('afterChange', function (event, slick, currentSlide) {
      const $thumbs = $(scope).find('.productView-thumbnail');
      $thumbs.removeClass('is-active');
      $thumbs.filter(`[data-thumb-index="${currentSlide}"]`).addClass('is-active');
    });

    const $firstThumb = $(scope).find('.productView-thumbnail[data-thumb-index="0"]');
    if ($firstThumb.length) $firstThumb.addClass('is-active');
  }

  function initThumbnailGallery(scope) {
    if (!scope) return;
    const thumbs = scope.querySelectorAll('.productView-thumbnail');
    if (!thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        const idx = parseInt(this.dataset.thumbIndex, 10);
        const $ = window.jQuery;
        const $nav = $ ? $(scope).find('.productView-nav') : null;

        if ($nav && $nav.hasClass('slick-initialized') && !isNaN(idx)) {
          $nav.slick('slickGoTo', idx);
        } else {
          const link = this.querySelector('[data-image]');
          const newSrc = link?.dataset.image;
          const mainSlot = scope.querySelector('[data-image-gallery-main]');
          const mainImg = mainSlot?.querySelector('[data-main-image]');
          if (mainImg && newSrc) mainImg.src = newSrc;
        }

        scope.querySelectorAll('.productView-thumbnail').forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');
      });
    });
  }

  function getProductData(scope) {
    const dataEl = scope?.querySelector('[data-quickshop-product-json]');
    if (!dataEl) return null;
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[halo-quick-shop] product JSON parse error', e);
      return null;
    }
  }

  function getSelectedOptions(scope) {
    const fields = scope.querySelectorAll('.halo-productOptions .form-field[data-option-index]');
    const opts = [];
    fields.forEach(field => {
      const idx = parseInt(field.dataset.optionIndex, 10);
      const checked = field.querySelector('input.form-radio:checked');
      opts[idx] = checked ? checked.value : null;
    });
    return opts;
  }

  function findVariant(product, selectedOptions) {
    if (!product || !Array.isArray(product.variants)) return null;
    return product.variants.find(v => {
      return selectedOptions.every((val, idx) => v.options[idx] === val);
    }) || null;
  }

  function updateAvailabilityState(scope, product, selectedOptions) {
    const fields = scope.querySelectorAll('.halo-productOptions .form-field[data-option-index]');
    fields.forEach(field => {
      const idx = parseInt(field.dataset.optionIndex, 10);
      const swatches = field.querySelectorAll('.form-option');
      swatches.forEach(opt => {
        const input = opt.querySelector('input.form-radio');
        const label = opt.querySelector('label.swatch');
        if (!input || !label) return;

        const testOptions = selectedOptions.slice();
        testOptions[idx] = input.value;

        const matchVariant = product.variants.find(v => {
          return testOptions.every((val, i) => {
            if (val == null) return true;
            return v.options[i] === val;
          });
        });

        label.classList.remove('disabled', 'soldout');
        if (!matchVariant) {
          label.classList.add('disabled');
        } else if (!matchVariant.available) {
          label.classList.add('soldout');
        }
      });
    });
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify?.money_format || '${{amount}}');
    }
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || '';
    return currency ? `${currency} ${amount}` : `$${amount}`;
  }

  function updatePriceDisplay(scope, variant) {
    const priceWrap = scope.querySelector('[data-quickshop-price]');
    if (!priceWrap || !variant) return;
    const price = formatMoney(variant.price);
    const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price
      ? formatMoney(variant.compare_at_price)
      : '';
    priceWrap.innerHTML = compareAt
      ? `<span class="price-item price-item--sale">${price}</span> <s class="price-item price-item--regular">${compareAt}</s>`
      : `<span class="price-item price-item--regular">${price}</span>`;
  }

  function updateAtcButton(scope, variant) {
    const btn = scope.querySelector('[data-quickshop-atc]');
    if (!btn) return;
    const label = btn.querySelector('.halo-atc-btn__label');
    if (!variant) {
      btn.disabled = true;
      btn.setAttribute('data-sold-out', '');
      if (label) label.textContent = 'Unavailable';
      return;
    }
    if (variant.available) {
      btn.disabled = false;
      btn.removeAttribute('data-sold-out');
      if (label) label.textContent = 'Add to cart';
    } else {
      btn.disabled = true;
      btn.setAttribute('data-sold-out', '');
      if (label) label.textContent = 'Sold out';
    }
  }

  function updateVariantInputs(scope, variant) {
    if (!variant) return;
    scope.querySelectorAll('[data-quickshop-variant-id], [data-quickshop-installment-id]').forEach(inp => {
      inp.value = variant.id;
    });
  }

  function updateSwatchActiveState(scope) {
    const fields = scope.querySelectorAll('.halo-productOptions .form-field[data-option-index]');
    fields.forEach(field => {
      const checked = field.querySelector('input.form-radio:checked');
      field.querySelectorAll('label.swatch').forEach(l => l.classList.remove('swatch-active'));
      if (checked) {
        const lbl = field.querySelector(`label.swatch[for="${checked.id}"]`);
        if (lbl) lbl.classList.add('swatch-active');
      }
      const labelInfo = field.querySelector('[data-selected-value]');
      if (labelInfo && checked) labelInfo.textContent = checked.value;
    });
  }

  function updateGalleryForVariant(scope, variant) {
    if (!variant || !variant.featured_image) return;
    const mediaId = variant.featured_image.id;
    const $ = window.jQuery;
    const $nav = $ ? $(scope).find('.productView-nav') : null;
    if (!$nav || !$nav.hasClass('slick-initialized')) return;

    let targetIndex = -1;
    $nav.find('.productView-image').each(function (i) {
      const slideMediaId = this.getAttribute('data-media-id');
      if (slideMediaId && String(slideMediaId) === String(mediaId)) {
        targetIndex = i;
      }
    });
    if (targetIndex >= 0) $nav.slick('slickGoTo', targetIndex);
  }

  function onOptionChange(scope) {
    const product = getProductData(scope);
    if (!product) return;

    const selected = getSelectedOptions(scope);
    const variant = findVariant(product, selected);

    updateSwatchActiveState(scope);
    updateAvailabilityState(scope, product, selected);

    if (variant) {
      updatePriceDisplay(scope, variant);
      updateVariantInputs(scope, variant);
      updateGalleryForVariant(scope, variant);
    }
    updateAtcButton(scope, variant);
  }

  function initVariantOptions(scope) {
    if (!scope) return;
    const radios = scope.querySelectorAll('.halo-productOptions input.form-radio');
    if (!radios.length) return;

    radios.forEach(radio => {
      radio.addEventListener('change', () => onOptionChange(scope));
    });

    const labels = scope.querySelectorAll('.halo-productOptions label.swatch');
    labels.forEach(label => {
      label.addEventListener('click', (e) => {
        const forId = label.getAttribute('for');
        if (!forId) return;
        const input = scope.querySelector(`#${CSS.escape(forId)}`);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function initQuantity(scope) {
    if (!scope) return;
    const wrap = scope.querySelector('[data-quickshop-qty]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-qty-input]');
    const minus = wrap.querySelector('[data-qty-minus]');
    const plus  = wrap.querySelector('[data-qty-plus]');
    if (!input) return;

    const clamp = () => {
      let v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      input.value = v;
    };

    minus?.addEventListener('click', () => {
      let v = parseInt(input.value, 10) || 1;
      if (v > 1) input.value = v - 1;
      clamp();
    });
    plus?.addEventListener('click', () => {
      let v = parseInt(input.value, 10) || 1;
      input.value = v + 1;
      clamp();
    });
    input.addEventListener('change', clamp);
    input.addEventListener('blur', clamp);
  }

  function showFeedback(scope, message, isError) {
    const fb = scope.querySelector('[data-quickshop-feedback]');
    if (!fb) return;
    fb.textContent = message;
    fb.hidden = false;
    fb.classList.toggle('is-error', !!isError);
    if (!isError) {
      setTimeout(() => { fb.hidden = true; fb.textContent = ''; }, 3500);
    }
  }

  async function refreshCartSections() {
    try {
      const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble', 'cart-notification'];
      const url = `${(window.Shopify?.routes?.root ?? '/')}?sections=${sectionsToFetch.join(',')}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();

      Object.keys(data).forEach(sectionId => {
        const html = data[sectionId];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        if (sectionId === 'cart-icon-bubble') {
          const bubble = document.getElementById('cart-icon-bubble');
          const newBubble = doc.querySelector('#shopify-section-cart-icon-bubble .cart-count-bubble') || doc.querySelector('.cart-count-bubble');
          if (bubble) {
            const existing = bubble.querySelector('.cart-count-bubble');
            if (newBubble) {
              if (existing) existing.replaceWith(newBubble);
              else bubble.appendChild(newBubble);
            } else if (existing) {
              existing.remove();
            }
          }
        }

        if (sectionId === 'cart-drawer') {
          const drawer = document.querySelector('cart-drawer');
          const newDrawerInner = doc.querySelector('cart-drawer .drawer__inner') || doc.querySelector('.drawer__inner');
          const oldDrawerInner = drawer?.querySelector('.drawer__inner');
          if (newDrawerInner && oldDrawerInner) {
            oldDrawerInner.replaceWith(newDrawerInner);
          }
        }
      });

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      console.warn('[halo-quick-shop] cart section refresh failed', e);
    }
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    if (drawer) {
      drawer.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
      return true;
    }
    return false;
  }

  function initAddToCart(scope) {
    if (!scope) return;
    const btn = scope.querySelector('[data-quickshop-atc]');
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;

      const variantInput = scope.querySelector('[data-quickshop-variant-id]');
      const qtyInput = scope.querySelector('[data-qty-input]');
      const variantId = variantInput?.value;
      const quantity = parseInt(qtyInput?.value, 10) || 1;

      if (!variantId) {
        showFeedback(scope, 'Please select a variant.', true);
        return;
      }

      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const root = window.Shopify?.routes?.root ?? '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: quantity }]
          })
        });

        const data = await res.json();
        if (!res.ok) {
          const msg = data?.description || data?.message || 'Could not add to cart.';
          showFeedback(scope, msg, true);
          return;
        }

        await refreshCartSections();
        showFeedback(scope, 'Added to cart!', false);

        setTimeout(() => {
          closePopup();
          openCartDrawer();
        }, 400);

      } catch (err) {
        console.error('[halo-quick-shop] ATC error', err);
        showFeedback(scope, 'Network error. Please try again.', true);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    });
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest(`[${TRIGGER_ATTR}]`);
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = trigger.dataset.productHandle || trigger.getAttribute(HANDLE_ATTR);
    if (!handle) return;
    loadQuickShop(handle);
  });

  document.addEventListener('click', function (e) {
    if (!isOpen) return;
    if (e.target.closest(`[${CLOSE_ATTR}]`)) {
      closePopup();
      return;
    }
    const inner = getPopup()?.querySelector('.halo-popup-wrapper');
    if (inner && !inner.contains(e.target) && isOpen) {
      closePopup();
    }
  });

  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' && isOpen) closePopup();
  });

  document.addEventListener('DOMContentLoaded', function () {
    const popup = getPopup();
    if (popup) popup.setAttribute('hidden', '');
  });

})();